/* Additive storefront enhancements; existing Shopify and Twiggy scripts stay intact. */
(() => {
  'use strict';

  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const initializedVideos = new WeakSet();
  const initializedSoundButtons = new WeakSet();
  const initializedIllustrations = new WeakSet();
  let revealObserver;
  let videoObserver;
  let headerResizeObserver;
  let scrollFrame;

  const reducedMotion = () => motionPreference.matches;

  const initializeReveals = (root = document) => {
    const elements = root.querySelectorAll('[data-twiggy-reveal]');

    if (!elements.length || reducedMotion() || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('twiggy-reveal-visible'));
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('twiggy-reveal-visible');
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }

    elements.forEach((element, index) => {
      if (element.classList.contains('twiggy-reveal-visible')) return;
      element.style.setProperty('--twiggy-stagger', `${Math.min(index % 4, 3) * 100}ms`);
      revealObserver.observe(element);
    });

    document.documentElement.classList.add('twiggy-motion-ready');
  };

  const initializeVideos = (root = document) => {
    const videos = root.matches?.('[data-twiggy-cinema]')
      ? root.querySelectorAll('video')
      : root.querySelectorAll('[data-twiggy-cinema] video');
    if (!videos.length) return;

    if (reducedMotion() || navigator.connection?.saveData === true) {
      videos.forEach((video) => video.pause());
      return;
    }

    if (!videoObserver && 'IntersectionObserver' in window) {
      videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting || document.visibilityState === 'hidden') {
            target.pause();
            return;
          }

          target.play()?.catch(() => { target.dataset.autoplayBlocked = 'true'; });
        });
      }, { threshold: 0.15 });
    }

    videos.forEach((video) => {
      if (initializedVideos.has(video)) return;
      initializedVideos.add(video);
      video.muted = true;
      video.playsInline = true;
      if (videoObserver) videoObserver.observe(video);
    });
  };

  const updateSoundControl = (cinema, muted) => {
    const button = cinema.querySelector('[data-twiggy-sound-toggle]');
    if (!button) return;

    const label = muted ? 'Turn video sound on' : 'Turn video sound off';
    button.dataset.state = muted ? 'muted' : 'unmuted';
    button.setAttribute('aria-label', label);
    button.setAttribute('aria-pressed', String(!muted));
    button.setAttribute('title', label);

    const visibleLabel = button.querySelector('[data-twiggy-sound-label]');
    if (visibleLabel) visibleLabel.textContent = muted ? 'Sound off' : 'Sound on';
  };

  const initializeSoundControls = (root = document) => {
    const cinemas = root.matches?.('[data-twiggy-cinema]')
      ? [root]
      : root.querySelectorAll('[data-twiggy-cinema]');

    cinemas.forEach((cinema) => {
      const button = cinema.querySelector('[data-twiggy-sound-toggle]');
      const videos = Array.from(cinema.querySelectorAll('video'));

      if (!button || !videos.length || initializedSoundButtons.has(button)) return;
      initializedSoundButtons.add(button);

      button.addEventListener('click', () => {
        const activeVideo = videos.find((video) => window.getComputedStyle(video).display !== 'none') || videos[0];
        const shouldMute = !activeVideo.muted;

        if (!shouldMute) {
          document.querySelectorAll('[data-twiggy-cinema]').forEach((otherCinema) => {
            if (otherCinema === cinema) return;
            otherCinema.querySelectorAll('video').forEach((otherVideo) => { otherVideo.muted = true; });
            updateSoundControl(otherCinema, true);
          });
        }

        videos.forEach((video) => {
          video.muted = shouldMute;
          if (!shouldMute && video.volume === 0) video.volume = 0.8;
        });

        updateSoundControl(cinema, shouldMute);

        if (!shouldMute && activeVideo.paused) {
          activeVideo.play()?.catch(() => {
            videos.forEach((video) => { video.muted = true; });
            updateSoundControl(cinema, true);
          });
        }
      });

      videos.forEach((video) => video.addEventListener('volumechange', () => updateSoundControl(cinema, video.muted)));
      updateSoundControl(cinema, true);
    });
  };

  const updateHeaderMeasurements = () => {
    const announcement = document.querySelector('.announcement-bar-section');
    const header = document.querySelector('.section-header .header-wrapper');
    const announcementHeight = announcement && window.getComputedStyle(announcement).display !== 'none'
      ? Math.round(announcement.getBoundingClientRect().height)
      : 0;
    const headerHeight = header ? Math.round(header.getBoundingClientRect().height) : 0;

    document.documentElement.style.setProperty('--twiggy-announcement-height', announcementHeight + 'px');

    if (headerHeight > 0) {
      document.documentElement.style.setProperty('--twiggy-header-height', headerHeight + 'px');
      document.documentElement.style.setProperty('--twiggy-safe-header-offset', (announcementHeight + headerHeight) + 'px');
    }
  };

  const initializeHeaderMeasurements = () => {
    updateHeaderMeasurements();

    if (!('ResizeObserver' in window)) return;

    if (!headerResizeObserver) {
      headerResizeObserver = new ResizeObserver(() => {
        window.requestAnimationFrame(updateHeaderMeasurements);
      });
    }

    headerResizeObserver.disconnect();

    document.querySelectorAll('.announcement-bar-section, .section-header .header-wrapper').forEach((element) => {
      headerResizeObserver.observe(element);
    });
  };

  const illustrationScenes = (root = document) => root.matches?.('.twiggy-cinema--san-francisco.twiggy-cinema--animated')
    ? [root]
    : root.querySelectorAll('.twiggy-cinema--san-francisco.twiggy-cinema--animated');

  const updateIllustrationScrollMotion = () => {
    const scenes = document.querySelectorAll('.twiggy-cinema--san-francisco.twiggy-cinema--animated');

    scenes.forEach((scene) => {
      if (reducedMotion() || navigator.connection?.saveData === true) {
        scene.style.setProperty('--twiggy-scroll-shift', '0px');
        return;
      }

      const bounds = scene.getBoundingClientRect();
      if (bounds.bottom < 0 || bounds.top > window.innerHeight) return;

      const progress = Math.min(1, Math.max(0, (window.innerHeight - bounds.top) / (window.innerHeight + bounds.height)));
      const travel = window.innerWidth <= 749 ? 14 : 10;
      const shift = (progress - 0.5) * travel;
      scene.style.setProperty('--twiggy-scroll-shift', shift.toFixed(2) + 'px');
    });
  };

  const initializeIllustrationMotion = (root = document) => {
    const scenes = illustrationScenes(root);
    const supportsPointerParallax = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    scenes.forEach((scene) => {
      if (initializedIllustrations.has(scene)) return;
      initializedIllustrations.add(scene);

      if (!supportsPointerParallax) return;

      let pointerFrame;
      let nextX = 0;
      let nextY = 0;

      const paintPointerPosition = () => {
        scene.style.setProperty('--twiggy-motion-x', nextX.toFixed(2) + 'px');
        scene.style.setProperty('--twiggy-motion-y', nextY.toFixed(2) + 'px');
        pointerFrame = undefined;
      };

      const queuePointerPosition = () => {
        if (!pointerFrame) pointerFrame = window.requestAnimationFrame(paintPointerPosition);
      };

      scene.addEventListener('pointermove', (event) => {
        if (reducedMotion() || navigator.connection?.saveData === true) return;
        const bounds = scene.getBoundingClientRect();
        const horizontal = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
        const vertical = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
        nextX = horizontal * -8;
        nextY = vertical * -5;
        queuePointerPosition();
      }, { passive: true });

      scene.addEventListener('pointerleave', () => {
        nextX = 0;
        nextY = 0;
        queuePointerPosition();
      }, { passive: true });
    });

    updateIllustrationScrollMotion();
  };

  const initialize = (root = document) => {
    initializeReveals(root);
    initializeVideos(root);
    initializeSoundControls(root);
    initializeHeaderMeasurements();
    initializeIllustrationMotion(root);
  };

  const start = () => {
    initialize();

    const updateHeader = () => {
      document.documentElement.classList.toggle('twiggy-header-scrolled', window.scrollY > 28);
      updateIllustrationScrollMotion();
      scrollFrame = undefined;
    };

    window.addEventListener('scroll', () => {
      if (!scrollFrame) scrollFrame = window.requestAnimationFrame(updateHeader);
    }, { passive: true });

    window.addEventListener('resize', () => {
      updateHeaderMeasurements();
      updateIllustrationScrollMotion();
    }, { passive: true });
    updateHeader();
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        document.querySelectorAll('[data-twiggy-cinema] video').forEach((video) => video.pause());
      }
    });

    motionPreference.addEventListener?.('change', () => {
      if (reducedMotion()) {
        revealObserver?.disconnect();
        document.querySelectorAll('[data-twiggy-reveal]').forEach((element) => element.classList.add('twiggy-reveal-visible'));
        document.querySelectorAll('.twiggy-cinema--san-francisco').forEach((scene) => {
          scene.style.setProperty('--twiggy-motion-x', '0px');
          scene.style.setProperty('--twiggy-motion-y', '0px');
          scene.style.setProperty('--twiggy-scroll-shift', '0px');
        });
      }
      initializeVideos();
      updateIllustrationScrollMotion();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
